// src/components/CentralOperacoes.js
//
// ─────────────────────────────────────────────────────────────────────────────
//  REAL-TIME FLEET TRACKING — versão auto-refresh + smooth movement
//  Baseada na versão blindada. Mudanças adicionais (marcadas abaixo):
//
//  [FIX-1..7]  Mantidas intactas da versão anterior (ver comentários originais).
//
//  [SMOOTH-1]  VehicleMarker — interpolação suave de posição via requestAnimationFrame.
//              O marcador desliza em ~700 ms (ease-in-out cúbico) em vez de "pular".
//  [SMOOTH-2]  A prop `position` do <Marker> é fixada na posição de montagem e NUNCA
//              é alterada. Toda movimentação subsequente é imperativa via marker.setLatLng(),
//              eliminando o snap/pulo causado pelo ciclo de render do react-leaflet.
//  [AUTO-1]    Loop recursivo com setTimeout substitui setInterval + fetchFleetPositions:
//              chama fetchInitialData() (a fonte da verdade — comprovadamente funciona)
//              sem jamais acumular requisições paralelas.
//  [AUTO-2]    Botão "Atualizar" substituído por indicador "AO VIVO" sempre girando.
//              Ainda é clicável para forçar atualização imediata.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { io } from 'socket.io-client';
import axios from 'axios';
import api from '../services/api';
import './CentralOperacoes.css';

import ModalCentralOperacoes from './ModalCentralOperacoes';
import ModalHistoricoRotas   from './ModalHistoricoRotas';
import RouteLayer            from './RouteLayer';
import ModalManutencaoWeb    from './ModalManutencaoWeb';

import {
  FaTrash, FaTimes, FaSearchLocation, FaMapMarkerAlt, FaStore, FaPhone,
  FaCheck, FaPlus, FaListUl, FaChevronRight, FaChevronLeft, FaCrosshairs,
  FaWrench, FaWarehouse, FaGasPump, FaUserShield, FaAmbulance,
  FaUtensils, FaTruck, FaPowerOff, FaSpinner, FaExclamationTriangle,
  FaClock, FaRoute, FaCarSide, FaFlagCheckered, FaEye, FaExpand, FaCompress,
  FaChevronDown, FaChevronUp, FaSync, FaSms, FaIdCard, FaStar,
} from 'react-icons/fa';

// ─── MAPBOX ───────────────────────────────────────────────────────────────────
const MAPBOX_ACCESS_TOKEN =
  'pk.eyJ1IjoiYmxuYXNjaW1lbnRvMjQwMjk4IiwiYSI6ImNtZ2lnYnc1NTA5MmEycG9pM2M5M3IwcWMifQ.JcuK1f9M-yZdwCeTXDx20Q';

// ─── CORREÇÃO LEAFLET ─────────────────────────────────────────────────────────
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl:       require('leaflet/dist/images/marker-icon.png'),
  shadowUrl:     require('leaflet/dist/images/marker-shadow.png'),
});

// ─── [FIX-1] ORIGEM ÚNICA DE BACKEND ─────────────────────────────────────────
const resolveApiBaseUrl = () => {
  const fromApi = api?.defaults?.baseURL;
  if (fromApi && /^https?:\/\//i.test(fromApi)) return fromApi.replace(/\/+$/, '');
  if (process.env.REACT_APP_API_BASE_URL) return process.env.REACT_APP_API_BASE_URL.replace(/\/+$/, '');
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin;
  return 'http://localhost:3001';
};
const API_BASE_URL = resolveApiBaseUrl();

// ─── [AUTO-1] INTERVALO DO LOOP RECURSIVO ────────────────────────────────────
// Ajuste fino: 3000 = mais ágil | 10000 = poupa banco
const AUTO_REFRESH_MS = 5000;

// ─── CATEGORIAS DE PARCEIROS ──────────────────────────────────────────────────
const ICONS_OPTIONS = [
  { type: 'oficina',     icon: FaWrench,     label: 'Oficina',      color: '#6f42c1' },
  { type: 'patio',       icon: FaWarehouse,  label: 'Pátio',        color: '#fd7e14' },
  { type: 'posto',       icon: FaGasPump,    label: 'Posto',        color: '#e83e8c' },
  { type: 'policia',     icon: FaUserShield, label: 'Base/Polícia', color: '#007bff' },
  { type: 'hospital',    icon: FaAmbulance,  label: 'Hospital',     color: '#dc3545' },
  { type: 'restaurante', icon: FaUtensils,   label: 'Alimentação',  color: '#28a745' },
];

// ─── STATUS ICONS / COLORS ────────────────────────────────────────────────────
const STATUS_COLORS = {
  livre:           '#10b981',
  em_deslocamento: '#f59e0b',
  em_atendimento:  '#ef4444',
  pausado:         '#fd7e14',
  manutencao:      '#ef4444',
  offline:         '#334155',
  _default:        '#3b82f6',
};

const STATUS_FA_ICONS = {
  livre:           'fa-truck',
  em_deslocamento: 'fa-location-arrow',
  em_atendimento:  'fa-tools',
  pausado:         'fa-coffee',
  manutencao:      'fa-wrench',
  offline:         'fa-power-off',
  _default:        'fa-truck',
};

const _buildDriverIcon = (statusKey) => {
  const color   = STATUS_COLORS[statusKey]   || STATUS_COLORS._default;
  const faClass = STATUS_FA_ICONS[statusKey] || STATUS_FA_ICONS._default;
  const isOnline = statusKey !== 'offline';
  return L.divIcon({
    className: 'driver-custom-icon',
    html: `
      <div style="position:relative;width:40px;height:40px;">
        ${isOnline ? `<div class="driver-pulse-ring" style="border-color:${color};"></div>` : ''}
        <div style="position:relative;z-index:2;background-color:${color};width:2.5rem;height:2.5rem;
          display:flex;justify-content:center;align-items:center;border-radius:50% 50% 50% 0;
          transform:rotate(-45deg);border:3px solid #FFF;box-shadow:0 4px 12px rgba(0,0,0,0.35);">
          <i class="fa ${faClass}" style="transform:rotate(45deg);font-size:1.1rem;color:white;"></i>
        </div>
      </div>`,
    iconSize: [40, 40], iconAnchor: [20, 40], popupAnchor: [0, -44],
  });
};

const DRIVER_ICONS = Object.fromEntries(
  Object.keys(STATUS_COLORS).map(k => [k, _buildDriverIcon(k)])
);

// ─── SUPPLIER ICON ────────────────────────────────────────────────────────────
const _buildSupplierIcon = (tipo) => {
  const config    = ICONS_OPTIONS.find(o => o.type === tipo) || ICONS_OPTIONS[0];
  const faClasses = {
    oficina:'fa-wrench', patio:'fa-warehouse', posto:'fa-gas-pump',
    policia:'fa-user-shield', hospital:'fa-ambulance', restaurante:'fa-utensils',
  };
  return L.divIcon({
    className: 'supplier-custom-icon',
    html: `<div style="background-color:${config.color};width:2rem;height:2rem;display:flex;
      justify-content:center;align-items:center;border-radius:50% 50% 50% 0;
      transform:rotate(-45deg);border:2px solid #FFF;box-shadow:0 2px 5px rgba(0,0,0,0.3);">
      <i class="fa ${faClasses[tipo]||'fa-map-marker'}" style="transform:rotate(45deg);font-size:0.9rem;color:white;"></i></div>`,
    iconSize: [32, 32], iconAnchor: [16, 32], popupAnchor: [0, -32],
  });
};

const SUPPLIER_ICONS = Object.fromEntries(ICONS_OPTIONS.map(o => [o.type, _buildSupplierIcon(o.type)]));

// ─── OFICINA CREDENCIADA ICON ─────────────────────────────────────────────────
const CRED_PALETA = ['#6f42c1','#fd7e14','#007bff','#dc3545','#28a745','#e83e8c','#20c997','#0dcaf0'];

const buildOficinaCredenciadaIcon = (oficina) => {
  const initials = (oficina.nome_fantasia || '?').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
  const cor = CRED_PALETA[oficina.id % CRED_PALETA.length];
  return L.divIcon({
    className: 'oficina-cred-icon',
    html: `
      <div style="position:relative;width:44px;height:44px;">
        <div style="position:absolute;inset:0;background:${cor};border-radius:50% 50% 50% 0;
          transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 4px 14px rgba(0,0,0,0.28);
          display:flex;align-items:center;justify-content:center;">
          <span style="transform:rotate(45deg);color:#fff;font-size:0.82rem;font-weight:800;
            letter-spacing:-0.02em;font-family:system-ui,sans-serif;">${initials}</span>
        </div>
        <div style="position:absolute;top:-4px;right:-4px;width:16px;height:16px;border-radius:50%;
          background:#fbbf24;border:2px solid #fff;display:flex;align-items:center;
          justify-content:center;font-size:0.55rem;z-index:3;">⭐</div>
      </div>`,
    iconSize: [44, 44], iconAnchor: [22, 44], popupAnchor: [0, -48],
  });
};

// ─── TIPOS DE PROBLEMA (ativação pela central) ────────────────────────────────
const TIPOS_MANUT_CENTRAL = [
  { key: 'motor',       label: 'Motor',      emoji: '🔧' },
  { key: 'pneu',        label: 'Pneu',       emoji: '⚫' },
  { key: 'bateria',     label: 'Bateria',    emoji: '🔋' },
  { key: 'acidente',    label: 'Acidente',   emoji: '🚨' },
  { key: 'combustivel', label: 'Combustív.', emoji: '⛽' },
  { key: 'outro',       label: 'Outro',      emoji: '⚠️' },
];

// ─── UTILITÁRIOS ──────────────────────────────────────────────────────────────
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return null;
  const toRad = v => (v * Math.PI) / 180;
  const R = 6371, dLat = toRad(lat2 - lat1), dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return (R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))).toFixed(1);
};

const sanitizePhone = (value = '') => String(value).replace(/\D/g, '');

const formatElapsed = (timestamp) => {
  if (!timestamp) return null;
  const s = Math.floor((Date.now() - timestamp) / 1000);
  if (s < 10)   return 'agora';
  if (s < 60)   return `${s}s atrás`;
  if (s < 3600) return `${Math.floor(s / 60)}m atrás`;
  return `${Math.floor(s / 3600)}h atrás`;
};

const getGpsTag = (lastSeenAt) => {
  if (!lastSeenAt) return { label: 'sem GPS', cls: '' };
  const s = Math.floor((Date.now() - lastSeenAt) / 1000);
  if (s < 15)  return { label: 'agora',                    cls: 'fresh' };
  if (s < 60)  return { label: `${s}s atrás`,              cls: 'fresh' };
  if (s < 300) return { label: `${Math.floor(s / 60)}m atrás`, cls: '' };
  return { label: `${Math.floor(s / 60)}m atrás`, cls: 'stale' };
};

// ─── [FIX-7] NORMALIZAÇÃO DE COORDENADA ──────────────────────────────────────
const toCoord = (v) => {
  if (v === undefined || v === null || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};
const hasCoords = (m) =>
  Number.isFinite(Number(m?.latitude)) && Number.isFinite(Number(m?.longitude));

// ─── [FIX-2] REDUCER ÚNICO DE INGESTÃO DA FROTA ──────────────────────────────
const applyFleetPatch = (prev, rawId, patch, now, allowNull = false) => {
  if (rawId === undefined || rawId === null) return prev;
  const id  = rawId;
  const cur = prev[id] || { id };
  const next = { ...cur, id };

  for (const k of Object.keys(patch)) {
    const v = patch[k];
    if (k === 'latitude' || k === 'longitude') {
      if (allowNull && v === null) { next[k] = null; continue; }
      const c = toCoord(v);
      if (c === undefined) continue;
      next[k] = c;
      continue;
    }
    if (v === undefined || v === null) continue;
    next[k] = v;
  }

  const posChanged =
    String(cur.latitude)  !== String(next.latitude) ||
    String(cur.longitude) !== String(next.longitude);
  next.lastSeenAt = posChanged ? now : (cur.lastSeenAt || now);
  return { ...prev, [id]: next };
};

// ─── CONGESTIONAMENTO ─────────────────────────────────────────────────────────
const CONGESTION_INFO = {
  low:     { label: 'Trânsito livre',    color: '#10b981' },
  moderate:{ label: 'Trânsito moderado', color: '#f59e0b' },
  heavy:   { label: 'Trânsito intenso',  color: '#ef4444' },
  severe:  { label: 'Congestionado',     color: '#9333ea' },
  unknown: { label: 'Calculando…',       color: '#3b82f6' },
};

// ─── STATUS BADGE ─────────────────────────────────────────────────────────────
const STATUS_TEXT = {
  livre: 'LIVRE', pausado: 'EM PAUSA', em_atendimento: 'OCUPADO',
  em_deslocamento: 'EM ROTA', manutencao: 'MANUTENÇÃO', offline: 'OFFLINE',
  'Na Fila': 'NA FILA', 'Em Andamento': 'EM ANDAMENTO', 'Agendado': 'AGENDADO',
};

const getBadgeClass = (status) => {
  const s = status?.trim() || '';
  if (s === 'Na Fila')      return 'status-livre';
  if (s === 'Em Andamento') return 'status-em_deslocamento';
  if (s === 'Agendado')     return 'status-pausado';
  return 'status-offline';
};

const StatusBadge = ({ status }) => (
  <span className={`status-badge ${STATUS_TEXT[status] ? `status-${status}` : getBadgeClass(status)}`}>
    {STATUS_TEXT[status] || status}
  </span>
);

// ─── [FIX-5] MAP CONTROLLER ───────────────────────────────────────────────────
const MapController = ({
  vehicles, selectedVehicle, selectedPartner,
  userPosition, focusTrigger, lastFocusedType,
  isMaximized, isCollapsed,
}) => {
  const map = useMap();
  const initialLoadRef  = useRef(true);
  const fittedFleetRef  = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize({ animate: true }), 350);
    return () => clearTimeout(t);
  }, [isMaximized, isCollapsed, map]);

  useEffect(() => {
    if (selectedVehicle && selectedPartner) return;
    if (lastFocusedType === 'partner' && selectedPartner?.latitude) {
      map.flyTo([selectedPartner.latitude, selectedPartner.longitude], 17, { animate: true, duration: 1.5 }); return;
    }
    if (lastFocusedType === 'vehicle' && selectedVehicle?.latitude && selectedVehicle?.longitude) {
      map.flyTo([selectedVehicle.latitude, selectedVehicle.longitude], 17, { animate: true, duration: 1.5 }); return;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVehicle, selectedPartner, focusTrigger, lastFocusedType]);

  useEffect(() => {
    if (lastFocusedType || (selectedVehicle && selectedPartner)) return;
    const valid = vehicles.filter(hasCoords);

    if (!fittedFleetRef.current && valid.length > 0) {
      if (valid.length === 1) {
        map.setView([Number(valid[0].latitude), Number(valid[0].longitude)], 15, { animate: true });
      } else {
        map.fitBounds(
          L.latLngBounds(valid.map(v => [Number(v.latitude), Number(v.longitude)])),
          { padding: [60, 60], maxZoom: 15 }
        );
      }
      fittedFleetRef.current = true;
      initialLoadRef.current = false;
      return;
    }

    if (initialLoadRef.current && userPosition && valid.length === 0) {
      map.setView(userPosition, 13, { animate: true });
      initialLoadRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles, userPosition, lastFocusedType, selectedVehicle, selectedPartner]);

  return null;
};

// ─── VEHICLE MARKER — [SMOOTH-1] [SMOOTH-2] ──────────────────────────────────
//
//  Estratégia de deslizamento suave:
//  • mountPosRef guarda a posição de montagem (primeira coords válidas).
//    Essa referência é passada como `position` ao <Marker> e NUNCA muda.
//    O react-leaflet não chama setLatLng quando a prop não muda → sem salto.
//  • Toda atualização de posição é tratada pelo useEffect que usa RAF para
//    interpolar de marker.getLatLng() (posição real atual, inclusive durante
//    animação em andamento) até o novo alvo em ~700 ms.
//  • Se uma nova posição chegar antes do fim da animação, ela é cancelada e
//    uma nova começa a partir de onde o marcador está agora.
//  • Hooks são todos chamados incondicionalmente; o guard `if (!isValid)` só
//    acontece no return final (cumpre Rules of Hooks).
//
const VehicleMarker = React.memo(
  ({ vehicle, onFocus, userRole, onOpenAdmin, onOpenHistory }) => {
    // ── Hooks primeiro — incondicionalmente (Rules of Hooks) ─────────────────
    const markerRef   = useRef(null); // instância L.Marker
    const animRef     = useRef(null); // handle RAF em andamento
    const mountPosRef = useRef(null); // [SMOOTH-2] posição de montagem — prop estável

    const lat     = Number(vehicle.latitude);
    const lng     = Number(vehicle.longitude);
    const isValid = Number.isFinite(lat) && Number.isFinite(lng);

    // Grava posição de montagem uma única vez (primeira coords válidas)
    if (isValid && !mountPosRef.current) {
      mountPosRef.current = [lat, lng];
    }

    // [SMOOTH-1] Animação imperativa via RAF — bypassa o setLatLng do react-leaflet
    useEffect(() => {
      if (!isValid || !markerRef.current) return;

      // Cancela animação anterior (posição chegou antes do fim)
      if (animRef.current) { cancelAnimationFrame(animRef.current); animRef.current = null; }

      // Ponto de partida: posição REAL do marcador (mesmo mid-animation)
      const { lat: fLat, lng: fLng } = markerRef.current.getLatLng();

      // Sem deslocamento relevante? não precisa animar
      if (Math.abs(fLat - lat) < 1e-7 && Math.abs(fLng - lng) < 1e-7) return;

      const DURATION = 700; // ms — ajuste: 400 = nervoso | 1000 = preguiçoso
      const t0 = performance.now();

      const tick = (now) => {
        const p  = Math.min((now - t0) / DURATION, 1);
        // Ease-in-out cúbico: arranca suave, corre no meio, freia no fim
        const ep = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
        markerRef.current?.setLatLng([
          fLat + (lat - fLat) * ep,
          fLng + (lng - fLng) * ep,
        ]);
        if (p < 1) animRef.current = requestAnimationFrame(tick);
        else       animRef.current = null;
      };

      animRef.current = requestAnimationFrame(tick);
      return () => {
        if (animRef.current) { cancelAnimationFrame(animRef.current); animRef.current = null; }
      };
    }, [lat, lng, isValid]);

    // ── Guard após hooks ──────────────────────────────────────────────────────
    if (!isValid) return null;

    const icon = DRIVER_ICONS[vehicle.status_operacional] || DRIVER_ICONS._default;

    return (
      <Marker
        ref={markerRef}
        position={mountPosRef.current}   // [SMOOTH-2] estável — react-leaflet não snapa
        icon={icon}
        eventHandlers={{ click: () => onFocus(vehicle) }}
      >
        <Popup>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 170 }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: 5, paddingBottom: 5, borderBottom: '1px solid #eee',
            }}>
              <strong style={{ fontSize: '1rem' }}>{vehicle.nome}</strong>
              <div style={{ display: 'flex', gap: '5px', marginLeft: 10 }}>
                <button
                  title="Histórico de Rotas"
                  onClick={e => { e.stopPropagation(); onOpenHistory(vehicle); }}
                  style={{
                    background: '#e0f2fe', color: '#0284c7', border: 'none',
                    borderRadius: '6px', width: 28, height: 28,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                  }}
                >
                  <FaRoute />
                </button>
                {['admin', 'admin_geral'].includes(userRole) && (
                  <button
                    title="Gerenciar"
                    onClick={e => { e.stopPropagation(); onOpenAdmin(vehicle); }}
                    style={{
                      background: '#334155', color: 'white', border: 'none',
                      borderRadius: '6px', width: 28, height: 28,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                    }}
                  >
                    <FaEye />
                  </button>
                )}
              </div>
            </div>
            <span style={{ marginTop: 5 }}>Status: <StatusBadge status={vehicle.status_operacional} /></span>
            {vehicle.lastSeenAt && (
              <span style={{ marginTop: 5, fontSize: '0.72rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4 }}>
                📡 GPS {formatElapsed(vehicle.lastSeenAt)}
              </span>
            )}
          </div>
        </Popup>
      </Marker>
    );
  },
  (prev, next) =>
    String(prev.vehicle.latitude)   === String(next.vehicle.latitude)   &&
    String(prev.vehicle.longitude)  === String(next.vehicle.longitude)  &&
    prev.vehicle.status_operacional === next.vehicle.status_operacional &&
    prev.vehicle.nome               === next.vehicle.nome               &&
    prev.vehicle.lastSeenAt         === next.vehicle.lastSeenAt,
);

// ══════════════════════════════════════════════════════════════════════════════
//  COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════════
export default function CentralOperacoes() {

  // ── DADOS ─────────────────────────────────────────────────────────────────
  const [frota,                setFrota]                = useState({});
  const [ordensAtivas,         setOrdensAtivas]         = useState([]);
  const [fornecedores,         setFornecedores]         = useState([]);
  const [oficinasCredenciadas, setOficinasCredenciadas] = useState([]);
  const [motoristasManutencao, setMotoristasManutencao] = useState({});
  const [manutencaoModal,      setManutencaoModal]      = useState(null);

  // ── ATIVAR MANUTENÇÃO PELA CENTRAL ────────────────────────────────────────
  const [ativarManutModal,   setAtivarManutModal]   = useState(null);
  const [ativarManutForm,    setAtivarManutForm]    = useState({ tipo: 'motor', desc: '' });
  const [ativarManutLoading, setAtivarManutLoading] = useState(false);

  // ── UI ────────────────────────────────────────────────────────────────────
  const [expandedDrivers,      setExpandedDrivers]      = useState({});
  const [osSelecionada,        setOsSelecionada]        = useState(null);
  const [motoristaSelecionado, setMotoristaSelecionado] = useState(null);
  const [parceiroFocado,       setParceiroFocado]       = useState(null);
  const [focusTrigger,         setFocusTrigger]         = useState(0);
  const [lastFocusedType,      setLastFocusedType]      = useState(null);
  const [loading,              setLoading]              = useState(true);
  const [isAssigning,          setIsAssigning]          = useState(false);
  const [activeTab,            setActiveTab]            = useState('os');
  const [showSuppliers,        setShowSuppliers]        = useState(true);
  const [showPartnersDrawer,   setShowPartnersDrawer]   = useState(false);
  const [isMaximized,          setIsMaximized]          = useState(true);
  const [isCollapsed,          setIsCollapsed]          = useState(false);
  const [userPosition,         setUserPosition]         = useState(null);
  const [userRole,             setUserRole]             = useState('');
  const [isRefreshing,         setIsRefreshing]         = useState(false);
  const [socketConnected,      setSocketConnected]      = useState(false);
  const [lastGlobalUpdate,     setLastGlobalUpdate]     = useState(null);
  const [ticker,               setTicker]               = useState(0);
  const [routeData,            setRouteData]            = useState(null);
  const [routeLoading,         setRouteLoading]         = useState(false);
  const [geocodedOfficinas,    setGeocodedOfficinas]    = useState({});
  const [geocodingIds,         setGeocodingIds]         = useState(new Set());
  const [showModal,            setShowModal]            = useState(false);
  const [buscandoEndereco,     setBuscandoEndereco]     = useState(false);
  const [newPartner,           setNewPartner]           = useState({ nome: '', telefone: '', latitude: '', longitude: '', tipo: 'oficina', endereco: '' });
  const [showDriverAdminModal, setShowDriverAdminModal] = useState(false);
  const [driverToEdit,         setDriverToEdit]         = useState(null);
  const [showHistoryModal,     setShowHistoryModal]     = useState(false);
  const [historyDriver,        setHistoryDriver]        = useState(null);
  const [showSmsModal,         setShowSmsModal]         = useState(false);
  const [smsData,              setSmsData]              = useState({ osId: '', telefone: '', loading: false, error: '' });
  const [showCredenciadas,     setShowCredenciadas]     = useState(true);

  // ── REFS ──────────────────────────────────────────────────────────────────
  // [AUTO-1] pollingRef e pollStartedRef removidos — não mais necessários
  const socketRef  = useRef(null);
  const apiBaseUrl = API_BASE_URL;

  // ── MEMOS ─────────────────────────────────────────────────────────────────
  const frotaArray           = useMemo(() => Object.values(frota), [frota]);
  const vehiclesWithLocation = useMemo(() => frotaArray.filter(hasCoords), [frotaArray]);
  const ordensPendentes      = useMemo(() => ordensAtivas.filter(os => os.status?.trim() === 'Na Fila'), [ordensAtivas]);
  const qtdManutencao        = useMemo(() => Object.keys(motoristasManutencao).length, [motoristasManutencao]);

  // ── CSS INJECTION ─────────────────────────────────────────────────────────
  useEffect(() => {
    const styleId = 'co-realtime-styles';
    if (document.getElementById(styleId)) return;
    const el = document.createElement('style');
    el.id = styleId;
    el.textContent = `
      .leaflet-marker-pane .leaflet-marker-icon {
        transition: none !important;
      }
      .driver-pulse-ring {
        position:absolute; width:50px; height:50px; border-radius:50% 50% 50% 0;
        transform:rotate(-45deg); border:3px solid; top:-5px; left:-5px;
        z-index:1; pointer-events:none; animation:driverPulse 2.4s ease-out infinite;
      }
      @keyframes driverPulse {
        0%   { transform:rotate(-45deg) scale(0.8);  opacity:0.9; }
        65%  { transform:rotate(-45deg) scale(1.65); opacity:0;   }
        100% { transform:rotate(-45deg) scale(0.8);  opacity:0;   }
      }
      .live-dot {
        display:inline-block; flex-shrink:0; width:8px; height:8px; border-radius:50%;
        background:#10b981; box-shadow:0 0 0 0 rgba(16,185,129,0.6);
        animation:livePulse 1.5s ease infinite;
      }
      .live-dot.disconnected { background:#ef4444; animation:none; box-shadow:none; }
      @keyframes livePulse {
        0%   { box-shadow:0 0 0 0   rgba(16,185,129,0.6); }
        70%  { box-shadow:0 0 0 8px rgba(16,185,129,0);   }
        100% { box-shadow:0 0 0 0   rgba(16,185,129,0);   }
      }
      .gps-elapsed-tag {
        display:inline-flex; align-items:center; gap:3px; font-size:0.64rem;
        font-weight:700; color:#64748b; background:#f1f5f9; border:1px solid #e2e8f0;
        border-radius:20px; padding:1px 7px; letter-spacing:0.03em;
      }
      .gps-elapsed-tag.fresh { color:#059669; background:#ecfdf5; border-color:#6ee7b7; }
      .gps-elapsed-tag.stale { color:#dc2626; background:#fef2f2; border-color:#fca5a5; }
      @keyframes fadeIn {
        from { opacity:0; transform:translateY(-6px); }
        to   { opacity:1; transform:translateY(0);    }
      }
      @keyframes manutPulse { 0%,100% { opacity:1; } 50% { opacity:0.55; } }
      .fleet-card-item { opacity:1 !important; }
      .fleet-card-manutencao {
        border-left:3px solid #ef4444 !important;
        background:rgba(239,68,68,0.03) !important;
      }
      .status-dot-large {
        width:36px; height:36px; border-radius:50%;
        display:flex; align-items:center; justify-content:center;
        font-size:1rem; color:white; flex-shrink:0;
        border:2px solid #ffffff; box-shadow:0 2px 8px rgba(0,0,0,0.12);
      }
      .status-dot-large.livre            { background:#10b981; }
      .status-dot-large.em_deslocamento  { background:#f59e0b; }
      .status-dot-large.em_atendimento   { background:#ef4444; }
      .status-dot-large.pausado          { background:#fd7e14; }
      .status-dot-large.manutencao       { background:#ef4444; animation:manutPulse 1.4s ease infinite; }
      .status-dot-large.offline          { background:#334155; }
      .fc-actions { display:flex; align-items:center; gap:5px; flex-shrink:0; }
      .fc-btn {
        width:30px; height:30px; border-radius:8px; border:none;
        display:flex; align-items:center; justify-content:center;
        cursor:pointer; transition:all 0.15s; font-size:0.72rem;
      }
      .fc-btn-locate  { background:#f0fdf4; color:#16a34a; border:1px solid #bbf7d0 !important; }
      .fc-btn-locate:hover  { background:#dcfce7; }
      .fc-btn-locate:disabled { opacity:0.4; cursor:default; }
      .fc-btn-manut-ativo  { background:#fee2e2; color:#ef4444; border:1px solid #fecaca !important; animation:manutPulse 1.5s ease infinite; }
      .fc-btn-manut-ativo:hover  { background:#fecaca; }
      .fc-btn-manut-idle   { background:#f1f5f9; color:#94a3b8; border:1px solid #e2e8f0 !important; }
      .fc-btn-manut-idle:hover   { background:#fee2e2; color:#ef4444; border-color:#fecaca !important; }
      .fc-btn-history { background:#e0f2fe; color:#0284c7; border:1px solid #bae6fd !important; }
      .fc-btn-history:hover { background:#bae6fd; }
      .fc-btn-admin   { background:#334155; color:#fff; border:1px solid #475569 !important; }
      .fc-btn-admin:hover   { background:#1e293b; }
    `;
    document.head.appendChild(el);
    return () => { const e = document.getElementById(styleId); if (e) e.remove(); };
  }, []);

  // ── TICKER ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const id = setInterval(() => setTicker(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // ── CALLBACKS ─────────────────────────────────────────────────────────────
  const clearRouteSelection = useCallback(() => {
    setMotoristaSelecionado(null); setParceiroFocado(null);
    setRouteData(null); setRouteLoading(false);
  }, []);

  const handleOpenAdminModal   = useCallback(d => { setDriverToEdit(d);  setShowDriverAdminModal(true); }, []);
  const handleOpenHistoryModal = useCallback(d => { setHistoryDriver(d); setShowHistoryModal(true); }, []);
  const updateDriverStatusLocally = useCallback((id, updates) =>
    setFrota(prev => ({ ...prev, [id]: { ...prev[id], ...updates } })), []);

  const handleFocusVehicle = useCallback(vehicle => {
    if (vehicle.status_operacional === 'manutencao') {
      const sessao = motoristasManutencao?.[vehicle.id];
      if (sessao) { setManutencaoModal(sessao); return; }
    }
    setMotoristaSelecionado(vehicle);
    setLastFocusedType(prev => prev === 'partner' ? prev : 'vehicle');
    setFocusTrigger(n => n + 1);
  }, [motoristasManutencao]);

  const handleLocateVehicle = useCallback(vehicle => {
    if (!hasCoords(vehicle)) return;
    setMotoristaSelecionado(vehicle);
    setLastFocusedType('vehicle');
    setFocusTrigger(n => n + 1);
  }, []);

  const handleFocusPartner = useCallback(partner => {
    setParceiroFocado(partner);
    setLastFocusedType(prev => prev === 'vehicle' ? prev : 'partner');
    setShowSuppliers(true);
    setFocusTrigger(n => n + 1);
  }, []);

  const toggleDriverExpand = useCallback((motoristaId, e) => {
    e.stopPropagation();
    setExpandedDrivers(prev => ({ ...prev, [motoristaId]: !prev[motoristaId] }));
  }, []);

  const handleRouteReady  = useCallback(({ distancia, duracao, congestionamento, fallback }) => {
    setRouteData({ distancia, duracao, congestionamento, fallback: !!fallback });
    setRouteLoading(false);
  }, []);

  const handleRouteClear = useCallback(() => { setRouteData(null); setRouteLoading(false); }, []);

  // ── ATIVAR MANUTENÇÃO PELA CENTRAL ────────────────────────────────────────
  const handleAtivarManutCentral = useCallback(async () => {
    if (!ativarManutModal) return;
    setAtivarManutLoading(true);
    try {
      const token = localStorage.getItem('token');
      await api.post('/api/operations/manutencao/ativar', {
        motoristaId:   ativarManutModal.motoristaId,
        problema_tipo: ativarManutForm.tipo,
        problema_desc: ativarManutForm.desc.trim(),
      }, { headers: { Authorization: `Bearer ${token}` } });
      setAtivarManutModal(null);
      setAtivarManutForm({ tipo: 'motor', desc: '' });
    } catch (err) {
      alert('Erro ao ativar manutenção: ' + (err.response?.data?.error || err.message));
    } finally {
      setAtivarManutLoading(false);
    }
  }, [ativarManutModal, ativarManutForm]);

  // ── GEOCODING LAZY ────────────────────────────────────────────────────────
  const handleFocusOfficina = useCallback(async (oficina) => {
    if (geocodedOfficinas[oficina.id]) {
      const { latitude, longitude } = geocodedOfficinas[oficina.id];
      setParceiroFocado({ ...oficina, latitude, longitude });
      setLastFocusedType('partner'); setFocusTrigger(n => n + 1); return;
    }
    const partes = [oficina.logradouro, oficina.numero, oficina.bairro, oficina.cidade, oficina.estado, 'Brasil'].filter(Boolean);
    if (partes.length < 2) { alert('Endereço incompleto.'); return; }
    setGeocodingIds(prev => new Set([...prev, oficina.id]));
    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(partes.join(', '))}.json?access_token=${MAPBOX_ACCESS_TOKEN}&country=BR&language=pt&limit=1`;
      const res = await axios.get(url);
      const feat = res.data?.features?.[0];
      if (!feat) { alert('Localização não encontrada.'); return; }
      const [lng, lat] = feat.center;
      const coords = { latitude: lat, longitude: lng };
      setGeocodedOfficinas(prev => ({ ...prev, [oficina.id]: coords }));
      setParceiroFocado({ ...oficina, ...coords });
      setLastFocusedType('partner'); setFocusTrigger(n => n + 1);
    } catch (err) {
      console.error('[geocoding oficina]', err.message);
      alert('Erro ao buscar localização da oficina.');
    } finally {
      setGeocodingIds(prev => { const next = new Set(prev); next.delete(oficina.id); return next; });
    }
  }, [geocodedOfficinas]);

  // ── FETCH COMPLETO (fonte da verdade) ──────────────────────────────────────
  const fetchInitialData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [fleetRes, ordensRes, suppRes, oficinasRes, manutRes] = await Promise.all([
        api.get('/api/operations/fleet-status',    { headers }),
        api.get('/api/ordens',                     { headers }),
        api.get('/api/map-resources/suppliers',    { headers }).catch(() => ({ data: [] })),
        api.get('/api/oficinas?limit=200',         { headers }).catch(() => ({ data: { data: [] } })),
        api.get('/api/operations/manutencao/ativas',{ headers }).catch(() => ({ data: [] })),
      ]);
      const now = Date.now();

      const frotaObj = (fleetRes.data || []).reduce((acc, m) => {
        acc[m.id] = {
          ...m,
          latitude:  toCoord(m.latitude)  ?? null,
          longitude: toCoord(m.longitude) ?? null,
          tempo_pausa_hoje: m.tempo_pausa_hoje || 0,
          lastSeenAt: now,
        };
        return acc;
      }, {});

      const ativas = ordensRes.data.filter(os =>
        !['Concluído','Finalizada','Cancelado','Lançamento Excluído'].includes(os.status?.trim())
      );
      const oficinasAtivas = (oficinasRes.data?.data || []).filter(o => o.ativo !== 0);
      const manutObj = (manutRes.data || []).reduce((acc, s) => {
        acc[s.motorista_id] = s;
        if (frotaObj[s.motorista_id]) frotaObj[s.motorista_id].status_operacional = 'manutencao';
        return acc;
      }, {});

      setFrota(frotaObj);
      setOrdensAtivas(ativas);
      setFornecedores(suppRes.data || []);
      setOficinasCredenciadas(oficinasAtivas);
      setMotoristasManutencao(manutObj);
      setLastGlobalUpdate(now);

      console.log(
        `[CentralOperacoes] fetchInitialData → ${Object.keys(frotaObj).length} motorista(s), ` +
        `${Object.values(frotaObj).filter(hasCoords).length} com GPS`
      );
    } catch (err) {
      console.error('[CentralOperacoes] fetchInitialData:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── [AUTO-1] LOOP RECURSIVO DE ATUALIZAÇÃO ────────────────────────────────
  //
  //  • Usa setTimeout recursivo: o próximo ciclo só é agendado DEPOIS que o
  //    atual termina. Nunca empilha requisições, nunca martela o banco.
  //  • Chama fetchInitialData() — a MESMA função do botão "Atualizar" que
  //    comprovadamente funciona — garantindo confiabilidade total.
  //  • `alive` flag garante limpeza perfeita no unmount (sem memory leak).
  //
  useEffect(() => {
    let alive = true;
    let timer;

    const loop = async () => {
      if (!alive) return;
      setIsRefreshing(true);
      try    { await fetchInitialData(); }
      catch  (e) { console.warn('[autorefresh]', e?.message); }
      finally {
        if (alive) {
          setIsRefreshing(false);
          timer = setTimeout(loop, AUTO_REFRESH_MS);
        }
      }
    };

    loop(); // primeiro disparo imediato — sem esperar AUTO_REFRESH_MS

    return () => { alive = false; clearTimeout(timer); };
  }, [fetchInitialData]);

  // handleManualRefresh mantido para: (1) botão AO VIVO clicável, (2) socket osUpdate
  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    await fetchInitialData();
    setTimeout(() => setIsRefreshing(false), 600);
  };

  // ── LIMPAR POSIÇÕES TRAVADAS (somente admin_geral) ────────────────────────
  const handleLimparPosicoes = useCallback(async () => {
    if (userRole !== 'admin_geral') return;
    if (!window.confirm('Limpar todas as posições travadas da frota?\nOs motoristas reaparecem assim que enviarem nova posição.')) return;
    try {
      const token = localStorage.getItem('token');
      await api.delete('/api/operations/admin/limpar-posicoes', { headers: { Authorization: `Bearer ${token}` } });
      setFrota(prev => {
        const now = Date.now();
        let acc = prev;
        Object.keys(prev).forEach(id => { acc = applyFleetPatch(acc, id, { latitude: null, longitude: null }, now, true); });
        return acc;
      });
      clearRouteSelection();
      setTimeout(fetchInitialData, 800);
    } catch (err) {
      alert('Erro ao limpar posições: ' + (err.response?.data?.error || err.message));
    }
  }, [userRole, fetchInitialData, clearRouteSelection]);

  const buscarCoordenadasPorEndereco = useCallback(async () => {
    if (!newPartner.endereco || newPartner.endereco.length < 5) return alert('Endereço curto.');
    setBuscandoEndereco(true);
    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(newPartner.endereco)}.json?access_token=${MAPBOX_ACCESS_TOKEN}&country=BR&language=pt&limit=1`;
      const res = await axios.get(url);
      if (res.data.features.length > 0) {
        const [lon, lat] = res.data.features[0].center;
        setNewPartner(p => ({ ...p, latitude: lat, longitude: lon, endereco: res.data.features[0].place_name }));
      } else alert('Local não encontrado.');
    } catch { alert('Erro ao buscar coordenadas.'); }
    finally { setBuscandoEndereco(false); }
  }, [newPartner.endereco]);

  // ── TOKEN / PERFIL ────────────────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('token'); if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setUserRole(payload.perfil || payload.role || payload.cargo || payload.tipo || '');
    } catch (e) { console.error('Token inválido', e); }
  }, []);

  // ── GEOLOCALIZAÇÃO ────────────────────────────────────────────────────────
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setUserPosition([pos.coords.latitude, pos.coords.longitude]),
        err => console.warn('Geolocalização:', err),
        { enableHighAccuracy: true }
      );
    }
  }, []);

  // ── [FIX-6] SOCKET — resiliente e diagnosticável ──────────────────────────
  useEffect(() => {
    fetchInitialData();
    const token = localStorage.getItem('token') || null;

    console.log('[CentralOperacoes] conectando socket em →', apiBaseUrl);
    const socket = io(apiBaseUrl, {
      transports:           ['websocket', 'polling'],
      reconnection:         true,
      reconnectionAttempts: Infinity,
      reconnectionDelay:    1500,
      reconnectionDelayMax: 8000,
      timeout:              12000,
      auth:                 { token },
    });
    socketRef.current = socket;

    const emitNewUserSafe = () => {
      try {
        if (!token) return;
        const payload = JSON.parse(atob(token.split('.')[1]));
        const userId   = payload?.id || payload?.userId || payload?.usuario_id || payload?.uid || null;
        const tenantId = payload?.active_tenant_id || payload?.tenantId || payload?.tenant_id || payload?.cliente_id || payload?.clienteId || null;
        if (userId) socket.emit('newUser', { id: userId, active_tenant_id: tenantId, clienteId: tenantId });
      } catch (e) { console.warn('[CentralOperacoes] emit newUser falhou:', e?.message); }
    };

    socket.on('connect', () => {
      console.log('[CentralOperacoes] ✅ socket conectado:', socket.id);
      setSocketConnected(true);
      emitNewUserSafe();
      fetchInitialData();
    });
    socket.on('disconnect', (reason) => {
      console.warn('[CentralOperacoes] ⚠️ socket desconectado:', reason);
      setSocketConnected(false);
    });
    socket.on('connect_error', (err) => {
      console.error('[CentralOperacoes] ❌ connect_error:', err?.message, '(URL:', apiBaseUrl, ')');
      setSocketConnected(false);
    });
    socket.io.on('reconnect', (n) => {
      console.log('[CentralOperacoes] 🔄 reconectado (tentativa', n, ')');
      emitNewUserSafe();
      fetchInitialData();
    });

    // [FIX-2/3] INGESTÃO ÚNICA — locationUpdate e statusUpdate → mesmo reducer
    const ingestSocket = (d) => {
      if (d?.motoristaId === undefined || d?.motoristaId === null) return;
      const now = Date.now();
      setFrota(prev => applyFleetPatch(prev, d.motoristaId, {
        nome:               d.nome,
        status_operacional: d.status,
        latitude:           d.latitude,
        longitude:          d.longitude,
      }, now));
      if (d.latitude != null && d.longitude != null) setLastGlobalUpdate(now);
    };

    socket.on('locationUpdate', ingestSocket);
    socket.on('statusUpdate',   ingestSocket);

    socket.on('osUpdate', data => {
      const statusLimpo  = data.status?.trim();
      const finalizados  = ['Concluído','Finalizada','Cancelado','Lançamento Excluído'];
      setOrdensAtivas(prev => {
        if (finalizados.includes(statusLimpo)) return prev.filter(os => os.id !== data.osId);
        if (prev.some(os => os.id === data.osId)) {
          return prev.map(os =>
            os.id === data.osId
              ? { ...os, status: statusLimpo, motorista_id: data.motoristaId || os.motorista_id }
              : os
          );
        }
        handleManualRefresh(); return prev;
      });
    });

    socket.on('manutencao:nova', payload => {
      setMotoristasManutencao(prev => ({ ...prev, [payload.motoristaId]: payload }));
      setFrota(prev => applyFleetPatch(prev, payload.motoristaId, {
        status_operacional: 'manutencao',
        nome:      payload.motoristaNome,
        latitude:  payload.latitude,
        longitude: payload.longitude,
      }, Date.now()));
    });

    socket.on('manutencao:desativada', ({ sessionId, motoristaId }) => {
      setMotoristasManutencao(prev => { const n = { ...prev }; delete n[motoristaId]; return n; });
      setManutencaoModal(prev => {
        if (prev?.sessionId === sessionId || prev?.motoristaId === motoristaId || prev?.motorista_id === motoristaId) return null;
        return prev;
      });
      setFrota(prev => applyFleetPatch(prev, motoristaId, { status_operacional: 'livre' }, Date.now()));
    });

    socket.on('posicoes:limpas', ({ motoristaId }) => {
      setFrota(prev => {
        const now = Date.now();
        if (motoristaId) {
          if (!prev[motoristaId]) return prev;
          return applyFleetPatch(prev, motoristaId, { latitude: null, longitude: null }, now, true);
        }
        let acc = prev;
        Object.keys(prev).forEach(id => { acc = applyFleetPatch(acc, id, { latitude: null, longitude: null }, now, true); });
        return acc;
      });
    });

    return () => {
      socket.off('locationUpdate', ingestSocket);
      socket.off('statusUpdate',   ingestSocket);
      socket.off('osUpdate');
      socket.off('connect');
      socket.off('disconnect');
      socket.off('connect_error');
      socket.io.off('reconnect');
      socket.off('manutencao:nova');
      socket.off('manutencao:desativada');
      socket.off('posicoes:limpas');
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, fetchInitialData]);

  // ── ATRIBUIÇÃO DE OS ──────────────────────────────────────────────────────
  const handleAssignOS = async (motoristaId, motoristaNome) => {
    if (!osSelecionada) return alert('Selecione uma OS.');
    if (!window.confirm(`Atribuir OS #${osSelecionada.id} a ${motoristaNome}?`)) return;
    setIsAssigning(true);
    try {
      const token = localStorage.getItem('token');
      await api.put('/api/operations/assign-os', { osId: osSelecionada.id, motoristaId }, { headers: { Authorization: `Bearer ${token}` } });
      setOrdensAtivas(p => p.map(os =>
        os.id === osSelecionada.id ? { ...os, status: 'Em Andamento', motorista_id: motoristaId } : os
      ));
      setOsSelecionada(null);
      setExpandedDrivers(prev => ({ ...prev, [motoristaId]: true }));
      setActiveTab('frota');
    } catch (err) { console.error(err); alert('Erro ao atribuir OS.'); }
    finally { setIsAssigning(false); }
  };

  // ── PARCEIROS ─────────────────────────────────────────────────────────────
  const handleSavePartner = async e => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const res = await api.post('/api/map-resources/suppliers', newPartner, { headers: { Authorization: `Bearer ${token}` } });
      setFornecedores(p => [...p, res.data]);
      setNewPartner({ nome: '', telefone: '', latitude: '', longitude: '', tipo: 'oficina', endereco: '' });
      setShowModal(false);
    } catch { alert('Erro ao salvar parceiro.'); }
  };

  const handleDeletePartner = async id => {
    if (!window.confirm('Excluir parceiro?')) return;
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/api/map-resources/suppliers/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setFornecedores(p => p.filter(x => x.id !== id));
    } catch { alert('Erro ao excluir.'); }
  };

  // ── SMS ───────────────────────────────────────────────────────────────────
  const openSmsModal = (os, e) => {
    e.stopPropagation();
    setSmsData({ osId: os.id, telefone: sanitizePhone(os.telefone_cliente || ''), loading: false, error: '' });
    setShowSmsModal(true);
  };
  const closeSmsModal = () => {
    if (smsData.loading) return;
    setShowSmsModal(false); setSmsData({ osId: '', telefone: '', loading: false, error: '' });
  };
  const handleConfirmSendSMS = async () => {
    if (!smsData.telefone) { setSmsData(prev => ({ ...prev, error: 'Informe um telefone válido.' })); return; }
    try {
      setSmsData(prev => ({ ...prev, loading: true, error: '' }));
      await api.post(`/api/ordens/${smsData.osId}/enviar-sms-rastreio`, {
        telefone_cliente: sanitizePhone(smsData.telefone), origem_disparo: 'manual',
      });
      alert('📱 SMS enviado com sucesso!'); closeSmsModal();
    } catch (err) {
      setSmsData(prev => ({ ...prev, loading: false, error: err.response?.data?.error || err.message || 'Erro ao enviar SMS.' }));
    }
  };

  // ── RENDER: Lista OS ──────────────────────────────────────────────────────
  const renderOSList = () => (
    <div className="os-list-container">
      {ordensPendentes.length === 0 ? (
        <div className="empty-state-box">
          <FaCheck style={{ fontSize: '2rem', marginBottom: 10, color: '#cbd5e1' }} />
          <p>Sem OS na fila no momento.</p>
        </div>
      ) : ordensPendentes.map(os => (
        <div
          key={os.id}
          className={`os-card-item ${osSelecionada?.id === os.id ? 'active' : ''}`}
          onClick={() => setOsSelecionada(os)}
        >
          <div className="os-card-top" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="os-id">#{os.id}</span>
            <StatusBadge status={os.status} />
          </div>
          <div className="os-card-body">
            <p className="os-desc">{os.descricao}</p>
            <div className="os-location"><FaMapMarkerAlt className="os-icon" /><span>{os.local_atendimento}</span></div>
          </div>
        </div>
      ))}
    </div>
  );

  // ── RENDER: Lista Frota ───────────────────────────────────────────────────
  const renderFleetList = () => (
    <div className="fleet-list-container">
      {frotaArray.length === 0 ? (
        <div className="empty-state-box">
          <FaPowerOff style={{ fontSize: '2rem', marginBottom: 10, color: '#cbd5e1' }} />
          <p>Nenhum motorista online.</p>
        </div>
      ) : frotaArray.map(m => {
        const hasGPS        = hasCoords(m);
        const distancia     = (osSelecionada && hasGPS && osSelecionada.latitude)
          ? calculateDistance(m.latitude, m.longitude, osSelecionada.latitude, osSelecionada.longitude) : null;
        const osDoMotorista = ordensAtivas.filter(os => os.motorista_id === m.id && os.status?.trim() !== 'Na Fila');
        const isExpanded    = expandedDrivers[m.id];
        const gpsTag        = getGpsTag(m.lastSeenAt);
        const emManutencao  = m.status_operacional === 'manutencao';
        const sessaoManut   = motoristasManutencao[m.id];
        const isAtivar      = ativarManutModal?.motoristaId === m.id;

        return (
          <div key={m.id} style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '6px' }}>
            <div
              className={`fleet-card-item ${motoristaSelecionado?.id === m.id ? 'active' : ''} ${emManutencao ? 'fleet-card-manutencao' : ''}`}
              onClick={() => handleFocusVehicle(m)}
              style={{ cursor: 'pointer' }}
            >
              <div className="fleet-content-wrapper">
                <div className="fleet-avatar-area" style={{ position: 'relative' }}>
                  <div className={`status-dot-large ${m.status_operacional || 'offline'}`}><FaTruck /></div>
                  {!hasGPS && <div className="no-gps-badge" title="Sem GPS"><FaExclamationTriangle /></div>}
                </div>
                <div className="fleet-info-area" style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                    <strong style={{ fontSize: '0.82rem', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.nome}
                    </strong>
                    <div className="fc-actions" onClick={e => e.stopPropagation()}>
                      <button
                        className="fc-btn fc-btn-locate"
                        title={hasGPS ? 'Focar no mapa' : 'Sem GPS'}
                        disabled={!hasGPS}
                        onClick={() => handleLocateVehicle(m)}
                      >
                        <FaCrosshairs />
                      </button>
                      {emManutencao && sessaoManut ? (
                        <button className="fc-btn fc-btn-manut-ativo" title="Ver emergência"
                          onClick={() => setManutencaoModal(sessaoManut)}><FaWrench /></button>
                      ) : (
                        <button className="fc-btn fc-btn-manut-idle" title="Ativar modo manutenção"
                          onClick={() => setAtivarManutModal({ motoristaId: m.id, motoristaNome: m.nome })}>
                          <FaWrench />
                        </button>
                      )}
                      <button className="fc-btn fc-btn-history" title="Histórico"
                        onClick={() => handleOpenHistoryModal(m)}><FaRoute /></button>
                      {['admin', 'admin_geral'].includes(userRole) && (
                        <button className="fc-btn fc-btn-admin" title="Gerenciar"
                          onClick={() => handleOpenAdminModal(m)}><FaEye /></button>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', marginTop: 4 }}>
                    <StatusBadge status={m.status_operacional} />
                    {distancia && <span className="distance-badge"><FaRoute /> {distancia} km</span>}
                    <span className={`gps-elapsed-tag ${gpsTag.cls}`} style={{ display: ticker >= 0 ? 'inline-flex' : 'none' }}>
                      📡 {gpsTag.label}
                    </span>
                  </div>
                  {emManutencao && sessaoManut?.problema_desc && (
                    <div style={{ fontSize: '0.67rem', color: '#ef4444', marginTop: 3, display: 'flex', alignItems: 'center', gap: 3 }}>
                      <FaWrench style={{ fontSize: '0.58rem', flexShrink: 0 }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>
                        {sessaoManut.problema_desc}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="fleet-actions" style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                {osDoMotorista.length > 0 && (
                  <button
                    onClick={e => toggleDriverExpand(m.id, e)}
                    style={{
                      border: '1px solid #e2e8f0', background: isExpanded ? '#f1f5f9' : '#fff',
                      color: '#64748b', cursor: 'pointer', padding: '5px 9px', borderRadius: '6px',
                      display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.2s',
                    }}
                  >
                    {isExpanded ? <FaChevronUp style={{ fontSize: '0.7rem' }} /> : <FaChevronDown style={{ fontSize: '0.7rem' }} />}
                    <span style={{ fontWeight: 'bold', fontSize: '0.72rem' }}>{osDoMotorista.length} OS</span>
                  </button>
                )}
                {!emManutencao && osSelecionada && osSelecionada.status === 'Na Fila' && m.status_operacional === 'livre' && (
                  <button className="btn-assign-action" onClick={e => { e.stopPropagation(); handleAssignOS(m.id, m.nome); }}>
                    {isAssigning ? <FaSpinner className="fa-spin" /> : <FaCheck />}
                    <span>Atribuir</span>
                  </button>
                )}
              </div>
            </div>

            {isAtivar && (
              <div
                style={{
                  marginLeft: 8, marginRight: 4, background: '#fff',
                  border: '1px solid #fecaca', borderRadius: 12, padding: '12px 14px',
                  display: 'flex', flexDirection: 'column', gap: 10,
                  animation: 'fadeIn 0.2s ease', boxShadow: '0 4px 16px rgba(239,68,68,0.10)',
                }}
                onClick={e => e.stopPropagation()}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FaWrench style={{ color: '#ef4444', fontSize: '0.75rem' }} />
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#7f1d1d' }}>
                      Ativar manutenção — {ativarManutModal?.motoristaNome?.split(' ')[0] || ''}
                    </span>
                  </div>
                  <button
                    onClick={() => { setAtivarManutModal(null); setAtivarManutForm({ tipo: 'motor', desc: '' }); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 2 }}
                  >
                    <FaTimes style={{ fontSize: '0.8rem' }} />
                  </button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {TIPOS_MANUT_CENTRAL.map(t => (
                    <button
                      key={t.key}
                      onClick={() => setAtivarManutForm(f => ({ ...f, tipo: t.key }))}
                      style={{
                        padding: '5px 10px', borderRadius: 8, fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
                        border: ativarManutForm.tipo === t.key ? '1.5px solid #ef4444' : '1.5px solid #e2e8f0',
                        background: ativarManutForm.tipo === t.key ? '#fee2e2' : '#f8fafc',
                        color: ativarManutForm.tipo === t.key ? '#dc2626' : '#64748b',
                        display: 'flex', alignItems: 'center', gap: 4, transition: 'all 0.12s',
                      }}
                    >
                      <span>{t.emoji}</span><span>{t.label}</span>
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  placeholder="Descrição opcional..."
                  value={ativarManutForm.desc}
                  onChange={e => setAtivarManutForm(f => ({ ...f, desc: e.target.value }))}
                  style={{
                    padding: '8px 12px', fontSize: '0.78rem', borderRadius: 8,
                    border: '1px solid #e2e8f0', color: '#0f172a', outline: 'none', background: '#f8fafc',
                  }}
                />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => { setAtivarManutModal(null); setAtivarManutForm({ tipo: 'motor', desc: '' }); }}
                    style={{
                      padding: '7px 14px', fontSize: '0.75rem', fontWeight: 600, borderRadius: 8,
                      border: '1px solid #e2e8f0', background: '#f8fafc', color: '#64748b', cursor: 'pointer',
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleAtivarManutCentral}
                    disabled={ativarManutLoading}
                    style={{
                      padding: '7px 16px', fontSize: '0.75rem', fontWeight: 700, borderRadius: 8, border: 'none',
                      background: ativarManutLoading ? '#e2e8f0' : '#dc2626',
                      color: ativarManutLoading ? '#94a3b8' : '#fff',
                      cursor: ativarManutLoading ? 'default' : 'pointer',
                      display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.15s',
                    }}
                  >
                    {ativarManutLoading
                      ? <><FaSpinner className="fa-spin" /> Ativando...</>
                      : <><FaWrench style={{ fontSize: '0.7rem' }} /> Ativar agora</>
                    }
                  </button>
                </div>
              </div>
            )}

            {isExpanded && osDoMotorista.length > 0 && (
              <div style={{
                padding: '8px', background: '#f8fafc', border: '1px solid #e2e8f0',
                borderRadius: '8px', marginLeft: '44px', display: 'flex', flexDirection: 'column',
                gap: '6px', animation: 'fadeIn 0.2s ease-in-out',
              }}>
                <span style={{
                  fontSize: '0.65rem', fontWeight: 'bold', color: '#94a3b8',
                  textTransform: 'uppercase', letterSpacing: '0.05em', paddingLeft: '4px',
                }}>
                  Ordens Atribuídas
                </span>
                {osDoMotorista.map(os => (
                  <div
                    key={os.id}
                    onClick={() => { setOsSelecionada(os); handleFocusVehicle(m); }}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '8px 10px', cursor: 'pointer', border: '1px solid #e2e8f0',
                      borderRadius: '6px', background: osSelecionada?.id === os.id ? '#eff6ff' : '#fff',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#bfdbfe'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = osSelecionada?.id === os.id ? '#bfdbfe' : '#e2e8f0'; }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.8rem', fontWeight: '800', color: '#0f172a' }}>#{os.id}</span>
                        <StatusBadge status={os.status} />
                        <button
                          title="Enviar Rastreio por SMS"
                          onClick={e => openSmsModal(os, e)}
                          style={{
                            background: '#38bdf8', color: 'white', border: 'none', borderRadius: '4px',
                            padding: '2px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center',
                            gap: '4px', fontSize: '0.7rem', fontWeight: 'bold',
                          }}
                        >
                          <FaSms /> SMS
                        </button>
                      </div>
                      <span style={{ fontSize: '0.72rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        📍 {os.local_atendimento}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // ── RENDER PRINCIPAL ──────────────────────────────────────────────────────
  return (
    <div className={`operacoes-container-full ${isMaximized ? 'maximized-view' : ''}`}>

      {/* Controles flutuantes */}
      <div className="map-maximize-control" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>

        {/* [AUTO-2] Indicador AO VIVO — substitui o botão "Atualizar".
            Spinner sempre girando: lento/cinza em idle, rápido/verde ao buscar dados.
            Clicável para forçar atualização imediata. */}
        <button
          className="btn-control-float"
          title={`Mapa atualiza a cada ${AUTO_REFRESH_MS / 1000}s — clique para forçar`}
          onClick={handleManualRefresh}
          style={{ display: 'flex', alignItems: 'center', gap: 5, paddingInline: 10 }}
        >
          <FaSync
            className="fa-spin"
            style={{
              fontSize: '0.72rem',
              color: isRefreshing ? '#10b981' : '#94a3b8',
              animationDuration: isRefreshing ? '0.5s' : '4s',
            }}
          />
          <span style={{ fontSize: '0.64rem', fontWeight: 700, color: '#10b981', letterSpacing: '0.05em' }}>
            AO VIVO
          </span>
        </button>

        {/* Botão limpar posições — apenas admin_geral */}
        {userRole === 'admin_geral' && (
          <button
            className="btn-control-float"
            title="Limpar posições travadas (Admin)"
            onClick={handleLimparPosicoes}
            style={{ color: '#ef4444', borderColor: '#fecaca' }}
          >
            <FaTrash />
          </button>
        )}

        <button className="btn-control-float" title={isMaximized ? 'Restaurar' : 'Maximizar'} onClick={() => setIsMaximized(v => !v)}>
          {isMaximized ? <FaCompress /> : <FaExpand />}
        </button>
        <button className="btn-control-float btn-close-full" title="Fechar"
          onClick={() => { setIsMaximized(false); setIsCollapsed(false); }}>
          <FaTimes />
        </button>
      </div>

      {isCollapsed && (
        <button className="btn-expand-float" onClick={() => setIsCollapsed(false)} title="Abrir">
          <FaChevronRight />
        </button>
      )}

      {/* Painel lateral */}
      <div className={`map-overlay-panel ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="panel-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <h2 style={{ margin: 0 }}>Central de Operações</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span className={`live-dot ${socketConnected ? '' : 'disconnected'}`} />
                <span style={{
                  fontSize: '0.67rem', fontWeight: 700,
                  color: socketConnected ? '#059669' : '#dc2626',
                  textTransform: 'uppercase', letterSpacing: '0.07em',
                }}>
                  {socketConnected ? 'AO VIVO' : 'RECONECTANDO...'}
                </span>
                {lastGlobalUpdate && (
                  <span style={{ fontSize: '0.62rem', color: '#94a3b8', marginLeft: 2 }}>
                    · {formatElapsed(lastGlobalUpdate)}
                  </span>
                )}
              </div>
            </div>
            <button className="btn-collapse-panel" onClick={() => setIsCollapsed(true)} title="Recolher">
              <FaChevronLeft />
            </button>
          </div>
        </div>

        {/* Banner manutenção */}
        {qtdManutencao > 0 && (
          <div
            onClick={() => setManutencaoModal(Object.values(motoristasManutencao)[0])}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px',
              background: '#fef2f2', borderBottom: '1px solid #fecaca', cursor: 'pointer',
              animation: 'manutPulse 2s ease infinite',
            }}
          >
            <FaWrench style={{ color: '#ef4444', fontSize: '0.8rem', flexShrink: 0 }} />
            <span style={{ flex: 1, fontSize: '0.75rem', fontWeight: 700, color: '#b91c1c' }}>
              {qtdManutencao} motorista{qtdManutencao > 1 ? 's' : ''} em manutenção
            </span>
            <FaChevronRight style={{ color: '#ef4444', fontSize: '0.7rem' }} />
          </div>
        )}

        <div className="tab-navigation">
          <button className={`tab-button ${activeTab === 'os' ? 'active' : ''}`} onClick={() => setActiveTab('os')}>
            OS <span className="count-badge">{ordensPendentes.length}</span>
          </button>
          <button className={`tab-button ${activeTab === 'frota' ? 'active' : ''}`} onClick={() => setActiveTab('frota')}>
            Frota <span className="count-badge">{frotaArray.length}</span>
          </button>
        </div>

        <div className="panel-filters">
          <div className="panel-toggles">
            <label className="partner-toggle-wrapper">
              <div className="partner-toggle-switch">
                <input type="checkbox" checked={showSuppliers} onChange={e => setShowSuppliers(e.target.checked)} />
                <span className="partner-toggle-track" />
              </div>
              <span className="partner-toggle-label">
                <FaStore className="toggle-icon" /> Parceiros
              </span>
              <span className="partner-count-chip">{showSuppliers ? fornecedores.length : 0}</span>
            </label>
            <label className="partner-toggle-wrapper">
              <div className="partner-toggle-switch cred">
                <input type="checkbox" checked={showCredenciadas} onChange={e => setShowCredenciadas(e.target.checked)} />
                <span className="partner-toggle-track" />
              </div>
              <span className="partner-toggle-label">
                <FaIdCard className="toggle-icon" /> Credenciados
              </span>
              <span className="partner-count-chip cred">{showCredenciadas ? oficinasCredenciadas.length : 0}</span>
            </label>
          </div>
          <button onClick={() => setShowModal(true)} className="btn-mini-add">
            <FaPlus /> Novo
          </button>
        </div>

        <div className="panel-scroll-area">
          {loading ? (
            <div className="empty-state-box">
              <FaSpinner className="fa-spin" style={{ fontSize: '2rem', color: '#94a3b8' }} />
            </div>
          ) : activeTab === 'os' ? renderOSList() : renderFleetList()}
        </div>
      </div>

      {/* Botão Parceiros */}
      <button className="partners-toggle-btn" onClick={() => setShowPartnersDrawer(true)} style={{ transform: 'translateX(-48px)' }}>
        <FaListUl /> Parceiros
      </button>

      {/* Drawer de parceiros */}
      <div className={`partners-drawer ${showPartnersDrawer ? 'open' : ''}`}>
        <div className="drawer-header">
          <h3>Parceiros Logísticos</h3>
          <button onClick={() => setShowPartnersDrawer(false)}><FaTimes /></button>
        </div>
        <div className="drawer-content">
          {fornecedores.length > 0 && (
            <>
              <div className="drawer-section-label">
                <FaMapMarkerAlt style={{ fontSize: '0.7rem' }} /> Pontos no Mapa
                <span className="drawer-section-count">{fornecedores.length}</span>
              </div>
              {fornecedores.map(p => {
                const conf = ICONS_OPTIONS.find(o => o.type === p.tipo) || ICONS_OPTIONS[0];
                const Ico  = conf.icon;
                return (
                  <div key={`map-${p.id}`} className="partner-list-item" onClick={() => handleFocusPartner(p)}>
                    <div className="partner-icon-box" style={{ background: conf.color }}><Ico /></div>
                    <div className="partner-info"><strong>{p.nome_fantasia}</strong><span>{conf.label}</span></div>
                    <div className="partner-actions-container">
                      <button title="Excluir" onClick={e => { e.stopPropagation(); handleDeletePartner(p.id); }}
                        style={{ background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '6px', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                );
              })}
            </>
          )}
          {showCredenciadas && oficinasCredenciadas.length > 0 && (
            <>
              <div className="drawer-section-label" style={{ marginTop: fornecedores.length > 0 ? 16 : 0 }}>
                <FaIdCard style={{ fontSize: '0.7rem' }} /> Rede Credenciada
                <span className="drawer-section-count">{oficinasCredenciadas.length}</span>
              </div>
              {oficinasCredenciadas.map(o => {
                const initials = (o.nome_fantasia || '?').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
                const cor       = CRED_PALETA[o.id % CRED_PALETA.length];
                const isLoading = geocodingIds.has(o.id);
                const isMapped  = !!geocodedOfficinas[o.id];
                const isFocused = parceiroFocado?.id === o.id;
                return (
                  <div
                    key={`cred-${o.id}`}
                    className={`partner-list-item credenciada ${isFocused ? 'cred-focused' : ''}`}
                    onClick={() => handleFocusOfficina(o)}
                    style={{ cursor: isLoading ? 'wait' : 'pointer' }}
                  >
                    <div className="partner-icon-box" style={{ background: cor, fontSize: '0.78rem', fontWeight: 800, position: 'relative' }}>
                      {isLoading ? <FaSpinner className="fa-spin" style={{ fontSize: '0.9rem', color: '#fff' }} /> : initials}
                      {isMapped && !isLoading && (
                        <span style={{ position: 'absolute', top: -4, right: -4, width: 14, height: 14, borderRadius: '50%', background: '#fbbf24', border: '2px solid #fff', fontSize: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>⭐</span>
                      )}
                    </div>
                    <div className="partner-info" style={{ flex: 1, minWidth: 0 }}>
                      <strong>{o.nome_fantasia}</strong>
                      <span>{o.especialidade || 'Oficina'}</span>
                      {(o.cidade || o.estado) && (
                        <span className="credenciada-location">📍 {[o.cidade, o.estado].filter(Boolean).join(' — ')}</span>
                      )}
                      {!isMapped && !isLoading && (
                        <span className="credenciada-location" style={{ color: '#3b82f6' }}>Clique para ver no mapa</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                      <span className="credenciada-badge-ativo"><FaStar style={{ fontSize: '0.55rem' }} /> Ativo</span>
                      {o.telefone && (
                        <a href={`tel:${o.telefone}`} onClick={e => e.stopPropagation()}
                          style={{ background: '#e0f2fe', color: '#0284c7', border: 'none', borderRadius: '6px', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', textDecoration: 'none' }}>
                          <FaPhone style={{ fontSize: '0.7rem' }} />
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          )}
          {fornecedores.length === 0 && oficinasCredenciadas.length === 0 && (
            <div style={{ padding: '32px 16px', textAlign: 'center', color: '#94a3b8' }}>
              <FaStore style={{ fontSize: '2rem', marginBottom: 10, opacity: 0.4 }} />
              <p style={{ margin: 0, fontSize: '0.85rem' }}>Nenhum parceiro cadastrado.</p>
            </div>
          )}
        </div>
      </div>

      {/* Mapa */}
      <main className="map-area-full">
        <MapContainer center={[-22.9068, -47.0616]} zoom={12} className="map" zoomControl={false} preferCanvas={false}>
          <TileLayer
            url={`https://api.mapbox.com/styles/v1/mapbox/streets-v11/tiles/{z}/{x}/{y}?access_token=${MAPBOX_ACCESS_TOKEN}`}
            attribution="© Mapbox" tileSize={512} zoomOffset={-1} keepBuffer={4}
          />
          {vehiclesWithLocation.map(m => (
            <VehicleMarker
              key={`moto-${m.id}`}
              vehicle={m}
              onFocus={handleFocusVehicle}
              userRole={userRole}
              onOpenAdmin={handleOpenAdminModal}
              onOpenHistory={handleOpenHistoryModal}
            />
          ))}
          {showSuppliers && fornecedores.map(f => {
            const lat = parseFloat(f.latitude), lng = parseFloat(f.longitude);
            if (isNaN(lat) || isNaN(lng)) return null;
            return (
              <Marker key={`supp-${f.id}`} position={[lat, lng]}
                icon={SUPPLIER_ICONS[f.tipo] || SUPPLIER_ICONS['oficina']}
                eventHandlers={{ click: () => handleFocusPartner(f) }}>
                <Popup>
                  <strong>{f.nome_fantasia}</strong><hr />
                  <small>{f.telefone}</small>
                  <p style={{ fontSize: '0.8rem', margin: 0 }}>{f.endereco}</p>
                </Popup>
              </Marker>
            );
          })}
          {osSelecionada?.latitude && (
            <Marker
              position={[osSelecionada.latitude, osSelecionada.longitude]}
              icon={L.icon({ iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png', iconSize: [25, 41], iconAnchor: [12, 41] })}
            >
              <Popup>OS #{osSelecionada.id}</Popup>
            </Marker>
          )}
          <RouteLayer
            origin={motoristaSelecionado}
            destination={parceiroFocado}
            onRouteReady={handleRouteReady}
            onRouteClear={handleRouteClear}
            onLoading={setRouteLoading}
          />
          {oficinasCredenciadas.map(o => {
            const coords = geocodedOfficinas[o.id]; if (!coords) return null;
            return (
              <Marker key={`cred-${o.id}`} position={[coords.latitude, coords.longitude]}
                icon={buildOficinaCredenciadaIcon(o)} eventHandlers={{ click: () => handleFocusOfficina(o) }}>
                <Popup>
                  <div style={{ minWidth: 180 }}>
                    <strong style={{ fontSize: '0.95rem' }}>{o.nome_fantasia}</strong>
                    <hr style={{ margin: '6px 0', borderColor: '#f1f5f9' }} />
                    <div style={{ fontSize: '0.8rem', color: '#475569', lineHeight: 1.6 }}>
                      {o.especialidade && <div>🔧 {o.especialidade}</div>}
                      {o.telefone     && <div>📞 {o.telefone}</div>}
                      {o.logradouro   && <div>📍 {[o.logradouro, o.numero, o.bairro].filter(Boolean).join(', ')}</div>}
                      {o.cidade       && <div style={{ color: '#94a3b8' }}>{o.cidade}{o.estado ? ` — ${o.estado}` : ''}</div>}
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <span style={{ background: '#ecfdf5', color: '#059669', border: '1px solid #a7f3d0', borderRadius: 999, padding: '2px 8px', fontSize: '0.65rem', fontWeight: 800 }}>⭐ Rede Credenciada</span>
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          })}
          <MapController
            vehicles={vehiclesWithLocation}
            selectedVehicle={motoristaSelecionado}
            selectedPartner={parceiroFocado}
            userPosition={userPosition}
            focusTrigger={focusTrigger}
            lastFocusedType={lastFocusedType}
            isMaximized={isMaximized}
            isCollapsed={isCollapsed}
          />
        </MapContainer>

        {/* Card de rota */}
        {motoristaSelecionado && parceiroFocado && (routeData || routeLoading) && (
          <div className="route-intelligence-card">
            <div className={`route-card-traffic-bar ${routeLoading ? 'traffic-loading' : `traffic-${routeData?.congestionamento || 'unknown'}`}`} />
            <div className="route-card-body">
              <button className="route-close-btn" onClick={clearRouteSelection}><FaTimes /></button>
              <div className="route-entity">
                <div className="route-entity-icon" style={{ background: '#3b82f6' }}><FaCarSide /></div>
                <strong>{motoristaSelecionado.nome}</strong><span>Motorista</span>
              </div>
              <div className="route-connector">
                <div className={`route-stats-pill ${routeLoading ? 'loading' : ''}`}>
                  <FaRoute style={{ color: '#64748b', fontSize: '0.78rem' }} />
                  {routeLoading ? <span className="stat-value" /> : <span>{routeData?.distancia ? `${routeData.distancia} km` : '—'}</span>}
                  <span className="stat-sep" />
                  <FaClock style={{ color: '#64748b', fontSize: '0.78rem' }} />
                  {routeLoading ? <span className="stat-value" /> : <span>{routeData?.duracao ? `~${routeData.duracao} min` : '—'}</span>}
                </div>
                {!routeLoading && routeData?.congestionamento && (
                  <div className={`route-traffic-badge traffic-${routeData.congestionamento}`}>
                    <span className="traffic-dot" />
                    {CONGESTION_INFO[routeData.congestionamento]?.label || 'Calculando…'}
                  </div>
                )}
                {routeLoading && (
                  <div className="route-traffic-badge traffic-unknown">
                    <FaSpinner className="fa-spin" style={{ fontSize: '0.68rem' }} /> Calculando rota…
                  </div>
                )}
                <div className={`route-connector-line traffic-${routeLoading ? 'unknown' : (routeData?.congestionamento || 'unknown')}`} />
              </div>
              <div className="route-entity">
                <div className="route-entity-icon" style={{ background: CONGESTION_INFO[routeData?.congestionamento || 'unknown']?.color || '#10b981' }}>
                  <FaFlagCheckered />
                </div>
                <strong>{parceiroFocado.nome_fantasia}</strong><span>Destino</span>
              </div>
            </div>
            {routeData && !routeData.fallback && (
              <div className="route-card-footer">
                <a
                  className="route-open-maps-btn"
                  href={`https://www.google.com/maps/dir/?api=1&origin=${motoristaSelecionado.latitude},${motoristaSelecionado.longitude}&destination=${parceiroFocado.latitude},${parceiroFocado.longitude}&travelmode=driving`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaMapMarkerAlt style={{ fontSize: '0.72rem' }} /> Abrir no Google Maps
                </a>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Modal: Novo parceiro */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content-modern">
            <div className="modal-header-modern">
              <div className="header-title-group">
                <div className="header-icon-box"><FaMapMarkerAlt /></div>
                <div><h3>Novo Ponto</h3><p>Cadastre bases, postos ou oficinas</p></div>
              </div>
              <button onClick={() => setShowModal(false)} className="close-btn-modern"><FaTimes /></button>
            </div>
            <form onSubmit={handleSavePartner} className="form-modern">
              <div className="modal-body-modern">
                <div className="modal-col form-col">
                  <h4 className="section-subtitle">Identificação</h4>
                  <div className="input-wrapper-modern full-width">
                    <label>Nome Fantasia</label>
                    <div className="input-inner">
                      {!newPartner.nome && <FaStore style={{ marginLeft: 10, color: '#94a3b8' }} />}
                      <input type="text" required value={newPartner.nome} onChange={e => setNewPartner(p => ({ ...p, nome: e.target.value }))} />
                    </div>
                  </div>
                  <div className="input-wrapper-modern full-width">
                    <label>Telefone</label>
                    <div className="input-inner">
                      {!newPartner.telefone && <FaPhone style={{ marginLeft: 10, color: '#94a3b8' }} />}
                      <input type="text" value={newPartner.telefone} onChange={e => setNewPartner(p => ({ ...p, telefone: e.target.value }))} />
                    </div>
                  </div>
                  <div className="input-wrapper-modern full-width search-wrapper">
                    <label>Localização</label>
                    <div className="input-inner">
                      {!newPartner.endereco && <FaSearchLocation style={{ marginLeft: 10, color: '#94a3b8' }} />}
                      <input
                        type="text"
                        value={newPartner.endereco}
                        onChange={e => setNewPartner(p => ({ ...p, endereco: e.target.value }))}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); buscarCoordenadasPorEndereco(); } }}
                      />
                      <button type="button" onClick={buscarCoordenadasPorEndereco} disabled={buscandoEndereco} style={{ marginRight: 5 }}>
                        {buscandoEndereco ? <FaSpinner className="fa-spin" /> : 'Buscar'}
                      </button>
                    </div>
                  </div>
                  <div className="coords-row">
                    <div className="input-wrapper-modern"><label>Lat</label>
                      <input type="text" readOnly value={newPartner.latitude} style={{ background: '#f8fafc' }} /></div>
                    <div className="input-wrapper-modern"><label>Lon</label>
                      <input type="text" readOnly value={newPartner.longitude} style={{ background: '#f8fafc' }} /></div>
                  </div>
                </div>
                <div className="modal-col visual-col">
                  <h4 className="section-subtitle">Categoria</h4>
                  <div className="icons-grid-modern">
                    {ICONS_OPTIONS.map(o => (
                      <div
                        key={o.type}
                        className={`icon-card-modern ${newPartner.tipo === o.type ? 'active' : ''}`}
                        onClick={() => setNewPartner(p => ({ ...p, tipo: o.type }))}
                        style={{ '--accent-color': o.color }}
                      >
                        <div className="icon-circle-modern"
                          style={{ background: newPartner.tipo === o.type ? o.color : '#f1f5f9', color: newPartner.tipo === o.type ? '#fff' : o.color }}>
                          <o.icon />
                        </div>
                        <span>{o.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer-modern">
                <button type="button" onClick={() => setShowModal(false)} className="btn-modern btn-ghost">Cancelar</button>
                <button type="submit" className="btn-modern btn-save">Cadastrar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: SMS */}
      {showSmsModal && (
        <div className="modal-overlay">
          <div className="modal-content-modern" style={{ maxWidth: '460px' }}>
            <div className="modal-header-modern">
              <div className="header-title-group">
                <div className="header-icon-box" style={{ background: '#e0f2fe', color: '#0284c7' }}><FaSms /></div>
                <div><h3>Enviar rastreio por SMS</h3><p>OS #{smsData.osId}</p></div>
              </div>
              <button onClick={closeSmsModal} className="close-btn-modern" disabled={smsData.loading}><FaTimes /></button>
            </div>
            <div className="form-modern">
              <div className="modal-body-modern" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div className="input-wrapper-modern full-width">
                  <label>Telefone do cliente</label>
                  <div className="input-inner">
                    <FaPhone style={{ marginLeft: 10, color: '#94a3b8' }} />
                    <input
                      type="text"
                      value={smsData.telefone}
                      onChange={e => setSmsData(prev => ({ ...prev, telefone: sanitizePhone(e.target.value) }))}
                      placeholder="11999999999"
                    />
                  </div>
                </div>
                {smsData.error && (
                  <div style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 12px', fontSize: '0.85rem' }}>
                    {smsData.error}
                  </div>
                )}
              </div>
              <div className="modal-footer-modern">
                <button type="button" onClick={closeSmsModal} className="btn-modern btn-ghost" disabled={smsData.loading}>Cancelar</button>
                <button type="button" className="btn-modern btn-save" onClick={handleConfirmSendSMS} disabled={smsData.loading}>
                  {smsData.loading ? <FaSpinner className="fa-spin" /> : <FaSms />}
                  <span style={{ marginLeft: 8 }}>Enviar SMS</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ModalCentralOperacoes
        isOpen={showDriverAdminModal}
        onClose={() => setShowDriverAdminModal(false)}
        driver={driverToEdit}
        onUpdateDriver={updateDriverStatusLocally}
      />

      {showHistoryModal && (
        <ModalHistoricoRotas motorista={historyDriver} onClose={() => setShowHistoryModal(false)} />
      )}

      {manutencaoModal && (
        <ModalManutencaoWeb
          sessao={manutencaoModal}
          parceiros={fornecedores}
          fornecedores={oficinasCredenciadas}
          socket={socketRef.current}
          onClose={() => setManutencaoModal(null)}
        />
      )}
    </div>
  );
}